/**
 * Progress poller for /ingest-progress.
 *
 * The DE/aiaccountant pipeline writes events to /tmp/axon_progress/{pid}.jsonl
 * server-side. We poll the gateway every 2s and dispatch new events to the UI.
 *
 * Once invoke() returns a progress_id, hand it to startProgressPoll() and you'll
 * get live events streamed into the conversation alongside SSE chat messages.
 */

import type { ProgressEvent } from '@/types/chat';
import { authHeader } from './auth';

export interface ProgressPollHandle {
  stop: () => void;
}

export interface ProgressPollCallbacks {
  onEvent: (ev: ProgressEvent) => void;
  onComplete?: () => void;
  onError?: (err: Error) => void;
}

interface ProgressResponse {
  progress_id: string;
  events: ProgressEvent[];
  complete: boolean;
  since: number;
  total: number;
}

export function startProgressPoll(
  progressId: string,
  cb: ProgressPollCallbacks,
  opts: { intervalMs?: number; maxConsecutiveErrors?: number; maxWaitMs?: number } = {},
): ProgressPollHandle {
  const intervalMs = opts.intervalMs ?? 2000;
  const maxConsecutiveErrors = opts.maxConsecutiveErrors ?? 30;
  // Stop polling after 10 minutes with no events — prevents zombie polls when
  // the upload failed silently and the progress file was never created.
  const maxWaitMs = opts.maxWaitMs ?? 10 * 60 * 1000;
  let stopped = false;
  let since = 0;
  let consecutiveErrors = 0;
  let firstEventSeen = false;
  const startedAt = Date.now();

  const tick = async () => {
    if (stopped) return;

    // Auto-stop if we've been waiting too long with no events at all.
    if (!firstEventSeen && Date.now() - startedAt > maxWaitMs) {
      stopped = true;
      cb.onError?.(new Error('Import timed out — no progress received. The file may be too large or the upload failed. Please try again.'));
      return;
    }

    try {
      const url = `/api/v1/aiaccountant/ingest-progress?progress_id=${encodeURIComponent(progressId)}&since=${since}`;
      const resp = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'application/json', ...authHeader() },
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const body = await resp.json();
      const data: ProgressResponse = body?.data ?? body;
      for (const ev of data.events ?? []) {
        firstEventSeen = true;
        try {
          cb.onEvent(ev);
        } catch (e) {
          console.error('progress onEvent threw', e);
        }
      }
      since = data.since ?? since;
      consecutiveErrors = 0;
      if (data.complete) {
        stopped = true;
        cb.onComplete?.();
        return;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors > maxConsecutiveErrors) {
        stopped = true;
        cb.onError?.(err as Error);
        return;
      }
    }
    if (!stopped) setTimeout(tick, intervalMs);
  };

  setTimeout(tick, 0);

  return { stop: () => { stopped = true; } };
}
